import axios from "axios"
import api from "api/api"
import { PDFDocument } from "pdf-lib"

export const fetchPDFData = async (pdfUrl) => {
  try {
    const response = await axios.get(pdfUrl, {
      responseType: 'blob',
      headers: { 'Accept': 'application/pdf' }
    })

    const contentDisposition = response.headers['content-disposition']
    let filename = 'doc.pdf' // filename por defecto

    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="(.+)"/)
      if (filenameMatch) {
        filename = filenameMatch[1]
      }
    }

    const pdf = await response.data.arrayBuffer()
    return { pdf, filename }

  } catch (error) {
    let msg = 'Error al obtener el PDF'
    if (error.response && error.response.data) {
      // si es un blob de error, convertir a texto
      if (error.response.data instanceof Blob) {
        try {
          const text = await error.response.data.text()
          const errorData = JSON.parse(text)
          msg = errorData.message || msg
        } catch {
          msg = 'Error al procesar la respuesta del servidor'
        }
      } else {
        msg = error.response.data.message || JSON.stringify(error.response.data)
      }
    } else if (error.message) {
      msg = error.message
    }
    throw new Error(msg)
  }
}

export const fetchProtectedPDFData = async (pdfUrl) => {
  try {
    const response = await api.get(pdfUrl, {
      responseType: "blob",
      headers: { Accept: "application/pdf" },
    })

    const contentDisposition = response.headers["content-disposition"]
    let filename = "doc.pdf"

    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="(.+)"/)
      if (filenameMatch) filename = filenameMatch[1]
    }

    const pdf = await response.data.arrayBuffer()
    return { pdf, filename }

  } catch (error) {
    let msg = "Error al obtener el PDF"

    if (error.response && error.response.data) {
      if (error.response.data instanceof Blob) {
        try {
          const text = await error.response.data.text()
          const errorData = JSON.parse(text)
          msg = errorData.message || msg
        } catch {
          msg = "Error al procesar la respuesta del servidor"
        }
      } else {
        msg = error.response.data.message || JSON.stringify(error.response.data)
      }
    } else if (error.message) {
      msg = error.message
    }

    throw new Error(msg)
  }
}

export const printPDFfromURL = async (pdfUrl) => {
  const response = await fetch(pdfUrl)
  const blob = await response.blob()

  const url = URL.createObjectURL(blob)
  const iframe = document.createElement('iframe')
  iframe.style.display = 'none'
  iframe.src = url
  document.body.appendChild(iframe)

  return new Promise((resolve) => {
    let printed = false;
    const executePrint = () => {
      if (printed) return;
      printed = true;
      console.log("Ejecutando print para", pdfUrl);
      try {
        iframe.contentWindow.focus()
        iframe.contentWindow.print()
      } catch (e) {
        console.error("Error intentando imprimir", e);
      }
      resolve()
      setTimeout(() => {
        URL.revokeObjectURL(url)
        document.body.removeChild(iframe)
      }, 60000)
    };

    iframe.onload = executePrint;
    setTimeout(executePrint, 1500);
  })
}

export const printMultiplePDFsFromURLs = async (pdfUrls) => {
  try {
    const mergedPdf = await PDFDocument.create();
    for (const pdfUrl of pdfUrls) {
      const response = await fetch(pdfUrl);
      const arrayBuffer = await response.arrayBuffer();
      const pdf = await PDFDocument.load(arrayBuffer);
      const copiedPages = await mergedPdf.copyPages(pdf, pdf.getPageIndices());
      copiedPages.forEach((page) => {
        mergedPdf.addPage(page);
      });
    }

    const mergedPdfBytes = await mergedPdf.save();
    const blob = new Blob([mergedPdfBytes], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);

    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    iframe.src = url;
    document.body.appendChild(iframe);

    return new Promise((resolve) => {
      let printed = false;
      const executePrint = () => {
        if (printed) return;
        printed = true;
        console.log("Ejecutando print para PDFs combinados");
        try {
          iframe.contentWindow.focus();
          iframe.contentWindow.print();
        } catch (e) {
          console.error("Error intentando imprimir combinados", e);
        }
        resolve();
        setTimeout(() => {
          URL.revokeObjectURL(url);
          document.body.removeChild(iframe);
        }, 60000);
      };

      iframe.onload = executePrint;
      setTimeout(executePrint, 1500);
    });
  } catch (error) {
    console.error("Error combinando e imprimiendo PDFs:", error);
  }
}

export const printPDF = (pdfContent) => {
  const blob = new Blob([pdfContent], { type: 'application/pdf' })
  const url = URL.createObjectURL(blob)

  // crear iframe oculto
  const iframe = document.createElement('iframe')
  iframe.style.display = 'none'
  iframe.src = url

  document.body.appendChild(iframe)

  // imprimir cuando se cargue
  iframe.onload = () => {
    iframe.contentWindow.focus()
    iframe.contentWindow.print()
    setTimeout(() => {
      URL.revokeObjectURL(url)
      document.body.removeChild(iframe)
    }, 60000)
  }
}

export const downloadPDF = (pdfContent, filename) => {
  const blob = new Blob([pdfContent], { type: 'application/pdf' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')

  link.href = url
  link.download = filename || 'doc.pdf'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)

  setTimeout(() => URL.revokeObjectURL(url), 100)
}

export const downloadExcel = (excelContent, filename) => {
  const blob = new Blob([excelContent], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')

  link.href = url
  link.download = filename || 'doc.xlsx'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)

  setTimeout(() => URL.revokeObjectURL(url), 100)
}

export const fetchProtectedExcelData = async (excelUrl) => {
  try {
    const response = await api.get(excelUrl, {
      responseType: "blob",
      headers: { Accept: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" },
    })

    const contentDisposition = response.headers["content-disposition"]
    let filename = "doc.xlsx"

    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="(.+)"/)
      if (filenameMatch) filename = filenameMatch[1]
    }

    const excel = await response.data.arrayBuffer()
    return { excel, filename }

  } catch (error) {
    let msg = "Error al obtener el Excel"

    if (error.response && error.response.data) {
      if (error.response.data instanceof Blob) {
        try {
          const text = await error.response.data.text()
          const errorData = JSON.parse(text)
          msg = errorData.message || msg
        } catch {
          msg = "Error al procesar la respuesta del servidor"
        }
      } else {
        msg = error.response.data.message || JSON.stringify(error.response.data)
      }
    } else if (error.message) {
      msg = error.message
    }

    throw new Error(msg)
  }
}

export const getPDFInfo = async (pdfContent) => {
  try {
    const pdfDoc = await PDFDocument.load(pdfContent)

    return {
      size: new Blob([pdfContent]).size,
      pageCount: pdfDoc.getPageCount(),
    }
  } catch (error) {
    return null
  }
}
